// The universal base: tokens, the type/colour foundation, and the primitives
// every surface renders (buttons, badges, fields, modals, toasts, skeletons).
// Nothing else belongs here.
//
// Everything surface-specific lives in styles/bundles/* and is imported by the
// layout that owns it, so Vite ships it in that layout's lazy chunk. That was
// always the intent of this file; for a while it was only half true, and the
// entry carried 85 stylesheets — 49 of them page-specific — which meant an
// anonymous buyer opening a customer's trust center downloaded the survey
// workspace, the page builder and the marketing site before first paint.
//
// The order here is the order these files had before the split, and every base
// file already loaded before every bundled file it shares a bare class with —
// checked, not assumed — so the cascade is unchanged. Adding a page stylesheet
// here would undo that; add it to the surface's bundle instead.
import '@/styles/tokens.css'
import '@/styles/logo.css'
import '@/styles/platform.css'
import '@/styles/components/card.css'
import '@/styles/components/buttons.css'
import '@/styles/components/badges.css'
import '@/styles/components/form-fields.css'
import '@/styles/components/modal-drawer.css'
import '@/styles/components/toasts.css'
import '@/styles/components/skeleton.css'

import { createInertiaApp, router } from '@inertiajs/react'
import { createRoot, hydrateRoot } from 'react-dom/client'
import { resolvePageComponent } from '@/lib/resolvePageComponent'

createInertiaApp({
  resolve: (name) => resolvePageComponent(name),
  // The library default is a #29d blue bar — a colour that exists nowhere in
  // the design system, on the most frequently seen piece of motion in the app.
  // The delay matters as much as the colour: most navigations complete well
  // under 200ms, so the bar should only appear when something is actually slow.
  progress: {
    color: '#8E2D3A',   // --claret-600
    delay: 150,
    showSpinner: false,
  },
  setup({ el, App, props }) {
    // Marketing and public trust-center pages arrive server-rendered, so the
    // markup is already in `el` and React must adopt it rather than throw it
    // away — mounting fresh would blank the page for a frame and undo the point
    // of rendering it on the server. Everything else mounts as before.
    if (el.hasChildNodes()) {
      hydrateRoot(el, <App {...props} />)
    } else {
      createRoot(el).render(<App {...props} />)
    }
  },
})

// When Inertia receives a non-Inertia response (e.g. session expired, server
// returned HTML instead of JSON), force a full-page redirect to login.
;(router.on as unknown as (e: string, cb: (event: { preventDefault: () => void }) => void) => void)('invalid', (event) => {
  event.preventDefault()
  // The staff console has its own identity and its own login — bouncing an
  // expired staff session to the member login would be a dead end.
  const admin = window.location.pathname.startsWith('/admin')
  window.location.href = admin ? '/admin/login' : '/auth/login'
})
